import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Notyf } from 'notyf';
import 'notyf/notyf.min.css';
const notyf = new Notyf({ duration: 7000, position: { x: 'right', y: 'bottom' } });

export const fetchPricingPlans = createAsyncThunk('pricing/fetchPricingPlans', async (token) => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/pricing`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });
    const data = await response.json();
    return data.data;
});

export const fetchPricingPlansHome = createAsyncThunk('pricingHome/fetchPricingPlansHome', async () => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/pricing`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const data = await response.json();
    return data.data; 
});

export const fetchSystemSettings = createAsyncThunk('settings/fetchSystemSettings', async (token) => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/settings`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });
    const data = await response.json();
    return data.settings; 
});

export const fetchSystemSettingsPublic = createAsyncThunk('settings/fetchSystemSettingsPublic', async () => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/settings_public`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            // 'Authorization': `Bearer ${token}`,
        },
    });
    const data = await response.json();
    return data.settings; 
});

export const updatePricingPlan = createAsyncThunk(
    'pricing/updatePricingPlan',
    async ({ id, updatedPlan, token }, { rejectWithValue }) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/pricing/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(updatedPlan),
            });
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to update pricing plan');
            }

            return { id, updatedPlan, message: data.message };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const pricingSlice = createSlice({
    name: 'pricing',
    initialState: {
        pricingPlans: null,
        status: 'idle',
        error: null,
        updateStatus: 'idle', 
        updateError: null,     
    },
    reducers: {
        setPricingPlans: (state, action) => {
            state.pricingPlans = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPricingPlans.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchPricingPlans.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.pricingPlans = action.payload;
            })
            .addCase(fetchPricingPlans.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(updatePricingPlan.pending, (state) => {
                state.updateStatus = 'loading';
            })
            .addCase(updatePricingPlan.fulfilled, (state, action) => {
                state.updateStatus = 'succeeded';
                state.pricingPlans = state.pricingPlans.map((plan) =>
                    plan.id === action.payload.id ? { ...plan, ...action.payload.updatedPlan } : plan
                );
            })
            .addCase(updatePricingPlan.rejected, (state, action) => {
                state.updateStatus = 'failed';
                state.updateError = action.payload;
            });
    },
});

const pricingSliceHome = createSlice({
    name: 'pricingHome',
    initialState: {
        pricingPlans: null,
        status: 'idle',
        error: null,   
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchPricingPlansHome.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchPricingPlansHome.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.pricingPlans = action.payload;
            })
            .addCase(fetchPricingPlansHome.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })
    },
});


export const confirmGeneralSettings = createAsyncThunk(
    'settings/confirmGeneralSettings',
    async ({ appName, picture, token }, { rejectWithValue }) => {
        const form = new FormData();
        form.append('name', appName);
        
        if (picture) {
            form.append('logo', picture);
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/add_settings`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: form
            });
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.message || 'Failed to update general settings');
            }

            return notyf.success(data.message); 
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const confirmPreferencesSettings = createAsyncThunk(
    'settings/confirmPreferencesSettings',
    async ({ timezone, language, token }, { rejectWithValue }) => {
        const request = {
            timezone,
            language: JSON.stringify(language)
        };

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/add_settings`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(request)
            });
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.message || 'Failed to update preferences settings');
            }

            return data.message; 
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const settingsSlice = createSlice({
    name: 'settings',
    initialState: {
        settingsFetched: null,
        settingsFetchedPublic: null,
        status: 'idle',
        statusPriv: 'idle',
        generalStatus: 'idle',
        preferencesStatus: 'idle',
        generalMessage: '',
        preferencesMessage: '',
        error: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchSystemSettings.pending, (state) => {
                state.statusPriv = 'loading';
            })
            .addCase(fetchSystemSettings.fulfilled, (state, action) => {
                state.statusPriv = 'succeeded';
                state.settingsFetched = action.payload;
            })
            .addCase(fetchSystemSettings.rejected, (state, action) => {
                state.statusPriv = 'failed';
                state.error = action.error.message;
            })
            .addCase(fetchSystemSettingsPublic.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchSystemSettingsPublic.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.settingsFetchedPublic = action.payload;
            })
            .addCase(fetchSystemSettingsPublic.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(confirmGeneralSettings.pending, (state) => {
                state.generalStatus = 'loading';
            })
            .addCase(confirmGeneralSettings.fulfilled, (state, action) => {
                state.generalStatus = 'succeeded';
                state.generalMessage =action.payload;
                state.error = null;
            })
            .addCase(confirmGeneralSettings.rejected, (state, action) => {
                state.generalStatus = 'failed';
                state.error = action.payload;
            })
            .addCase(confirmPreferencesSettings.pending, (state) => {
                state.preferencesStatus = 'loading';
            })
            .addCase(confirmPreferencesSettings.fulfilled, (state, action) => {
                state.preferencesStatus = 'succeeded';
                state.preferencesMessage =action.payload;
                state.error = null; 
            })
            .addCase(confirmPreferencesSettings.rejected, (state, action) => {
                state.preferencesStatus = 'failed';
                state.error = action.payload;
            });
    },
});




export const pricingReducer= pricingSlice.reducer;
export const pricingHomeReducer= pricingSliceHome.reducer;
export const settingsReducer= settingsSlice.reducer;
export const { setPricingPlans } = pricingSlice.actions;


